import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractService } from 'shared/services';
import toast from 'shared/utils/toast';
import { IResetResidualUnitsPreviewRow } from '../../types';

interface UseResetResidualUnitsPreviewParams {
  contractId?: string;
  onApplied?: () => void;
}

export const useResetResidualUnitsPreview = ({
  contractId,
  onApplied
}: UseResetResidualUnitsPreviewParams) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<IResetResidualUnitsPreviewRow[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<number[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const openDialog = async () => {
    if (!contractId) return;
    setOpen(true);
    setIsLoadingPreview(true);
    try {
      const res = await contractService.previewResetResidualUnits(+contractId);
      const previewRows = (res?.rows ?? []) as IResetResidualUnitsPreviewRow[];
      setRows(previewRows);
      setSelectedLineIds(previewRows.map((row) => row.id));
    } catch (error) {
      console.error('Errore durante il caricamento anteprima reset unità:', error);
      toast.error(t('pages.contract.resetResidualUnits.previewError'));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const toggleLine = (lineId: number) => {
    setSelectedLineIds((prev) =>
      prev.includes(lineId) ? prev.filter((id) => id !== lineId) : [...prev, lineId]
    );
  };

  const selectAll = () => {
    setSelectedLineIds(rows.map((row) => row.id));
  };

  const deselectAll = () => {
    setSelectedLineIds([]);
  };

  const allSelected = rows.length > 0 && selectedLineIds.length === rows.length;

  const apply = async () => {
    if (!contractId) return;
    setIsApplying(true);
    try {
      const res = await contractService.applyResetResidualUnits(+contractId, selectedLineIds);
      toast.success(
        t('pages.contract.resetResidualUnits.success', {
          count: res?.updated_lines ?? 0
        })
      );
      setOpen(false);
      onApplied?.();
    } catch (error) {
      console.error('Errore durante applicazione reset unità:', error);
      toast.error(t('pages.contract.resetResidualUnits.applyError'));
    } finally {
      setIsApplying(false);
    }
  };

  return {
    open,
    rows,
    selectedLineIds,
    isLoadingPreview,
    isApplying,
    allSelected,
    openDialog,
    closeDialog,
    toggleLine,
    selectAll,
    deselectAll,
    apply
  };
};
